import type { PublishedBrief } from 'fym-shared/src/releases/types';
import { compareReleaseVersions } from 'fym-shared/src/releases/versions';

/**
 * The rules of the Music24 web "What's new" panel (spec 2026-10-20 What's new §7, the Quizado
 * `WhatsNewRules`). Pure functions: the hooks give them the stored values and the server answers.
 */

/** True when `remote` is a newer version than `own`. An empty value is never newer. */
export function isNewerVersion(remote: string, own: string): boolean {
  if (remote === '' || own === '') {
    return false;
  }
  return compareReleaseVersions(remote, own) > 0;
}

/**
 * Web has no install. A browser is "fresh" when it has no stored "last seen" value for this user
 * and had no session when the page loaded: the person signed up or signed in during this visit.
 * A person who was signed in at page load and has no value upgrades from a build before this
 * feature, so the panel shows.
 */
export function isFreshInstall(input: { hasStoredLastSeen: boolean; hadSessionAtBoot: boolean }): boolean {
  return !input.hasStoredLastSeen && !input.hadSessionAtBoot;
}

/** Whether the panel opens after an update. It needs one entry or more to show. */
export function shouldOpenAfterUpdate(input: {
  running: string;
  lastSeen: string | null;
  freshInstall: boolean;
  hasEntries: boolean;
}): boolean {
  if (input.running === '' || !input.hasEntries || input.freshInstall) {
    return false;
  }
  // No value and not fresh: an upgrade from a build before this feature.
  if (input.lastSeen === null) {
    return true;
  }
  return isNewerVersion(input.running, input.lastSeen);
}

/** True when the running version can have notes that this person did not see. */
export function mayHaveUnseenNotes(running: string, lastSeen: string | null): boolean {
  return running !== '' && (lastSeen === null || isNewerVersion(running, lastSeen));
}

/** The stored "last seen" value only moves forward. */
export function nextLastSeen(current: string | null, version: string): string {
  if (current === null || isNewerVersion(version, current)) {
    return version;
  }
  return current;
}

/** The answer of `releases.get`: a successful answer (a brief or null), or no answer yet. */
export type BriefAnswer = { status: 'success'; brief: PublishedBrief | null } | { status: 'pending' | 'error' };

/**
 * A successful answer wins, null included: null means "not published" (or unpublished), so the
 * cache must go. Only a call that did not land (loading, offline, an error) uses the cache.
 */
export function resolveCache(answer: BriefAnswer, cached: PublishedBrief | null): PublishedBrief | null {
  if (answer.status === 'success') {
    return answer.brief;
  }
  return cached;
}

/**
 * What the What's new host does for a new build: nothing yet, the plain prompt of
 * `appUpdate/AppUpdateNotice`, or its update panel.
 */
export type UpdatePrompt = 'none' | 'plain' | 'panel';

/** The state of the notes of a newer version. */
export type UpdateNotes = 'pending' | 'none' | 'some';

/**
 * Decides what a new build shows (spec §7 "Update available", design whats-new a3):
 * - no answer from version.json, the same build, or a build whose panel the person closed: nothing;
 * - a new build of the same (or an older) version: the plain prompt;
 * - a newer version with notes, on a quiet Home: the panel; on another page, or with no notes:
 *   the plain prompt;
 * - a newer version while the notes load: nothing yet.
 */
export function updatePrompt(input: {
  remote: { version: string; buildId: string } | null;
  ownVersion: string;
  ownBuildId: string;
  laterBuildId: string | null;
  notes: UpdateNotes;
  quietHome: boolean;
}): UpdatePrompt {
  const { remote } = input;
  if (remote === null || remote.buildId === input.ownBuildId || remote.buildId === input.laterBuildId) {
    return 'none';
  }
  if (!isNewerVersion(remote.version, input.ownVersion)) {
    return 'plain';
  }
  if (input.notes === 'pending') {
    return 'none';
  }
  return input.notes === 'some' && input.quietHome ? 'panel' : 'plain';
}

/**
 * What the host tells `AppUpdateNotice` (`appUpdate/appUpdateStore`): `plain` lets its toast show,
 * `panel` keeps it away, and null makes it wait (the notes load). A build whose panel the person
 * closed with Later stays with the panel, so the plain toast does not ask again.
 */
export function updateHandover(input: {
  prompt: UpdatePrompt;
  buildId: string | null;
  laterBuildId: string | null;
}): 'plain' | 'panel' | null {
  if (input.buildId === null) {
    return null;
  }
  if (input.prompt === 'none') {
    return input.buildId === input.laterBuildId ? 'panel' : null;
  }
  return input.prompt;
}
